import { useContext, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TableContainer,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, Send, WarningAmberOutlined } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

export const PhishedNid = ({ userId }) => {
  const [state, setState] = useState({ error: null, waiting: false, success: false });

  const { authState } = useOktaAuth();
  const { userModule, setUserModule } = useContext(UserModuleContext);

  const suspendAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/suspend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: authState.accessToken.accessToken },
        body: JSON.stringify({ networkId: userModule.userId, comment: `Account ${userModule.userId} suspended`, callerId: authState.accessToken.claims.sub }),
      });

      await res.json();
      setUserModule((prev) => ({ ...prev, userData: { ...prev.userData, "Okta Status": "SUSPENDED" } }));
      setState((prev) => ({ ...prev, waiting: false, success: true }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  const toggleAdStatus = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/ad/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: authState.accessToken.accessToken },
        body: JSON.stringify({ networkId: userModule.userData["Network ID"], comment: "AD Status Changed", callerId: authState.accessToken.claims.sub }),
      });

      await res.json();
      setUserModule((prev) => ({ ...prev, adAccountEnabled: !userModule.adAccountEnabled }));
      setState((prev) => ({ ...prev, waiting: false, success: true }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  const phishedNid = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/phished`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: authState.accessToken.accessToken },
        body: JSON.stringify({ networkId: userId, callerId: authState.accessToken.claims.sub }),
      });

      await res.json();
      setUserModule((prev) => ({
        ...prev,
        userData: { ...prev.userData, "Okta Status": "SUSPENDED" },
        adAccountEnabled: !userModule.adAccountEnabled,
      }));
      setState((prev) => ({ ...prev, waiting: false, success: true }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Action failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (state.success) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Action Completed</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Account {userId} has been suspended and their password is expired.
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Warning Banner */}
      <Box sx={{ backgroundColor: "#fff3e0", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <WarningAmberOutlined sx={{ color: "#e65100" }} fontSize="small" />
        <Typography variant="body2" sx={{ color: "#e65100", fontWeight: 600 }}>
          These actions will restrict account access immediately.
        </Typography>
      </Box>

      {/* ── Section: Phished NID Options ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Phished NID Options
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {userModule.userData["Okta Status"] === "ACTIVE" && (
          <Button
            variant="outlined"
            endIcon={<Send />}
            onClick={suspendAccount}
            sx={{
              justifyContent: "space-between",
              borderColor: WSU_RED,
              color: WSU_RED,
              fontWeight: 500,
              "&:hover": { backgroundColor: "#fdf0f2", borderColor: "#8a0c25" },
            }}
          >
            Suspend Okta Account
          </Button>
        )}
        {userModule.adAccountEnabled && (
          <Button
            variant="outlined"
            endIcon={<Send />}
            onClick={toggleAdStatus}
            sx={{
              justifyContent: "space-between",
              borderColor: WSU_RED,
              color: WSU_RED,
              fontWeight: 500,
              "&:hover": { backgroundColor: "#fdf0f2", borderColor: "#8a0c25" },
            }}
          >
            Deactivate AD Account
          </Button>
        )}
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={phishedNid}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
          }}
        >
          Full Phished NID
        </Button>
      </Box>

    </TableContainer>
  );
};
